import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Icon,
  Text,
  BlockStack,
  InlineStack,
  Spinner,
  Select,
  TextField,
  Collapsible,
} from '@shopify/polaris';
import {
  ChevronLeftMinor,
  ChevronRightMinor,
  ImageMajor,
  FileMinor,
  MagicMajor,
  CircleTickMajor,
  RefreshMajor,
} from '@shopify/polaris-icons';
import { authService } from '../../services/authService';
import { generationsService } from '../../services/generationsService';
import { getSceneVersions, activateSceneVersion } from '../../services/sceneVersionService';
import {
  montageJobsService,
  saveBlobDownload,
} from '../../services/montageJobsService';
import { getPreviewUrl } from '../../utils/imageUtils';
import { buildGenerationScenePreviewItems, groupGenerationPreviewScenes } from '../../utils/generationRecovery';
import AuthenticatedAssetImage from '../AuthenticatedAssetImage';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const parseModelOptions = (rawValue) => {
  const raw = String(rawValue || '');
  const values = raw
    .split(/\r?\n|,/)
    .map((value) => value.trim())
    .filter(Boolean);
  return Array.from(new Set(values));
};

const extractEnabledModelOptions = (settingsPayload = {}) => {
  const catalogValues = Array.isArray(settingsPayload?.REGEN_MODEL_CATALOG)
    ? settingsPayload.REGEN_MODEL_CATALOG
      .filter((item) => item && item.enabled !== false && item?.capabilities?.regeneration !== false)
      .map((item) => {
        const value = String(item.value || '').trim();
        if (!value) return null;
        const label = String(item.label || value).trim() || value;
        return { label, value };
      })
      .filter(Boolean)
    : [];

  if (catalogValues.length > 0) {
    const deduped = [];
    const seen = new Set();
    catalogValues.forEach((item) => {
      const key = String(item.value || '').trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      deduped.push(item);
    });
    return deduped;
  }

  return parseModelOptions(settingsPayload?.REGEN_MODEL_OPTIONS || '').map((value) => ({ label: value, value }));
};

const normalizeProviderValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'vertex' || normalized === 'gemini') return 'vertex';
  if (normalized === 'comfyui' || normalized === 'flux2') return 'comfyui';
  if (normalized === 'fal-ai' || normalized === 'fal.ai' || normalized === 'fal-flux-max' || normalized === 'fal-ai-flux2-dev' || normalized === 'fal-flux2-dev' || normalized === 'flux2-dev') return 'fal-ai';
  return normalized;
};

const providerLabel = (value) => {
  const normalized = normalizeProviderValue(value);
  if (normalized === 'vertex') return 'Vertex';
  if (normalized === 'comfyui') return 'ComfyUI';
  if (normalized === 'fal-ai') return 'FAL.AI';
  return normalized || 'Unknown';
};

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 12,
  lineHeight: '18px',
  border: '1px solid #dfe3e8',
  background: '#f6f6f7',
  color: '#202223',
};

const missingBadgeStyle = {
  position: 'absolute',
  top: 12,
  left: 12,
  zIndex: 2,
  padding: '5px 10px',
  borderRadius: 999,
  background: '#d72c0d',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const getResolvedModelLabel = (responseData = {}) => {
  const customResolved = String(responseData?.customModelResolved || '').trim();
  if (customResolved) return customResolved;
  const modelUsed = responseData?.modelUsed;
  if (!modelUsed || typeof modelUsed !== 'object') return '';
  return String(modelUsed.multi || modelUsed.single || modelUsed.model || '').trim();
};

const ViewGenerationModal = ({
  isOpen,
  onClose,
  selectedGeneration,
  setToastMessage,
  setToastActive
}) => {
  const [currentGenerationImageIndex, setCurrentGenerationImageIndex] = useState(0);
  const [isDownloadingReadablePdf, setIsDownloadingReadablePdf] = useState(false);
  const [isDownloadingReadablePdf38x19, setIsDownloadingReadablePdf38x19] = useState(false);
  const [isDownloadingPrintedPdf, setIsDownloadingPrintedPdf] = useState(false);
  const [isDownloadingPrintedPdfNoMontage, setIsDownloadingPrintedPdfNoMontage] = useState(false);
  const [regeneratingBySceneOrder, setRegeneratingBySceneOrder] = useState({});
  const activeSceneRegenerationsRef = useRef(new Set());
  const [regeneratedImageUrlsBySceneOrder, setRegeneratedImageUrlsBySceneOrder] = useState({});
  const [isApproving, setIsApproving] = useState(false);
  const [isApprovingFace, setIsApprovingFace] = useState(false);
  const [isRegeneratingFace, setIsRegeneratingFace] = useState(false);
  const [isGeneratingTrial, setIsGeneratingTrial] = useState(false);
  const [isContinuingGeneration, setIsContinuingGeneration] = useState(false);
  const [customPrompts, setCustomPrompts] = useState({});
  const [selectedModelsBySceneKey, setSelectedModelsBySceneKey] = useState({});
  const [regenerationModelOptions, setRegenerationModelOptions] = useState([]);
  const [defaultRegenerationProvider, setDefaultRegenerationProvider] = useState('vertex');
  const [defaultRegenerationModel, setDefaultRegenerationModel] = useState('');
  const [regenerationMetaBySceneKey, setRegenerationMetaBySceneKey] = useState({});
  const [expandedCustomPrompt, setExpandedCustomPrompt] = useState({});
  const [sceneVersionsBySceneKey, setSceneVersionsBySceneKey] = useState({});
  const [expandedVersionsBySceneKey, setExpandedVersionsBySceneKey] = useState({});
  const [activatingVersionIdBySceneKey, setActivatingVersionIdBySceneKey] = useState({});
  const [previewVersionIdBySceneKey, setPreviewVersionIdBySceneKey] = useState({});
  const [generationCostOverride, setGenerationCostOverride] = useState(null);
  const [expandedEditBySceneKey, setExpandedEditBySceneKey] = useState({});
  const [editPromptsBySceneKey, setEditPromptsBySceneKey] = useState({});
  const [editModelsBySceneKey, setEditModelsBySceneKey] = useState({});
  const [editingBySceneKey, setEditingBySceneKey] = useState({});
  const activeSceneEditsRef = useRef(new Set());
  const [isGeneratingGiftBox, setIsGeneratingGiftBox] = useState(false);
  const [giftBoxImageUrl, setGiftBoxImageUrl] = useState(null);
  const [isDownloadingCoverPdf, setIsDownloadingCoverPdf] = useState(false);
  const [isDownloadingReadablePdfNocover, setIsDownloadingReadablePdfNocover] = useState(false);
  const [isDownloadingMontagedPdfNoCover, setIsDownloadingMontagedPdfNoCover] = useState(false);
  const [isDownloadingMontagedBookletPdf, setIsDownloadingMontagedBookletPdf] = useState(false);
  const [generationDetails, setGenerationDetails] = useState(null);
  const [montageType, setMontageType] = useState('standard');
  const [montageJob, setMontageJob] = useState(null);
  const [isSubmittingMontage, setIsSubmittingMontage] = useState(false);
  const [downloadingMontageArtifact, setDownloadingMontageArtifact] = useState(null);
  const generationStoryType = generationDetails?.story?.comfy_flow
    || generationDetails?.story_type
    || generationDetails?.comfy_flow
    || selectedGeneration?.story?.comfy_flow
    || selectedGeneration?.story_type;
  const isDynamicStory = generationStoryType === 'dynamic_story';

  useEffect(() => {
    if (!isOpen || !selectedGeneration?.generation_id) {
      setMontageJob(null);
      return undefined;
    }

    let mounted = true;
    let timer;
    const load = async () => {
      try {
        const job = await montageJobsService.get(
          selectedGeneration.generation_id,
          montageType
        );
        if (!mounted) return;
        setMontageJob(job);
        timer = window.setTimeout(load, 3000);
      } catch (error) {
        if (mounted) setMontageJob(null);
        timer = window.setTimeout(load, 3000);
      }
    };
    load();

    return () => {
      mounted = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [isOpen, selectedGeneration?.generation_id, montageType]);

  const storySlug = String(
    selectedGeneration?.story_slug ||
    selectedGeneration?.story?.slug ||
    generationDetails?.story_slug ||
    generationDetails?.story?.slug ||
    ''
  ).toLowerCase();

  const defaultProfileKey = selectedGeneration?.story?.montage_profiles?.default
    || generationDetails?.story?.montage_profiles?.default
    || selectedGeneration?.story?.default_montage_profile
    || generationDetails?.story?.default_montage_profile
    || selectedGeneration?.story?.montage_profile_key
    || generationDetails?.story?.montage_profile_key;

  const isColoringBookA4 = defaultProfileKey === 'coloring-book-a4'
    || storySlug.includes('colored-book')
    || storySlug.includes('coloring-book');

  const montageTypeOptions = useMemo(() => [
    { label: 'Hard cover', value: 'standard' },
    { label: 'Booklet', value: 'booklet' },
    {
      label: isColoringBookA4 ? 'Single A4 Sheets (Coloring Book)' : 'Pristol Express',
      value: 'pristol_express'
    },
  ], [isColoringBookA4]);

  useEffect(() => {
    if (isColoringBookA4) {
      setMontageType('pristol_express');
    }
  }, [isColoringBookA4]);

  const regenerationModelSelectOptions = useMemo(() => {
    const storyOverrideModel = String(selectedGeneration?.story?.regeneration_model_override || '').trim();
    const generationModelUsed = String(selectedGeneration?.generation_model_used || '').trim();
    const options = [...regenerationModelOptions];
    const values = new Set(options.map((item) => item.value));
    if (defaultRegenerationModel && !values.has(defaultRegenerationModel)) {
      options.push({ label: defaultRegenerationModel, value: defaultRegenerationModel });
      values.add(defaultRegenerationModel);
    }
    if (storyOverrideModel && !values.has(storyOverrideModel)) {
      options.push({ label: storyOverrideModel, value: storyOverrideModel });
      values.add(storyOverrideModel);
    }
    if (generationModelUsed && !values.has(generationModelUsed)) {
      options.push({ label: generationModelUsed, value: generationModelUsed });
    }
    const items = [
      { label: 'Use story/global default', value: '' },
      ...options,
    ];
    return items;
  }, [regenerationModelOptions, defaultRegenerationModel, selectedGeneration]);

  // Scope modal width override to this modal only via body class while open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('view-generation-modal-open');
    } else {
      document.body.classList.remove('view-generation-modal-open');
    }
    return () => {
      document.body.classList.remove('view-generation-modal-open');
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;

    const loadRegenerationModelSettings = async () => {
      try {
        const response = await fetch(`${SERVER_URL}/settings/generation/regeneration-models`, {
          headers: authService.getAuthHeaders(),
        });
        if (!response.ok) return;
        const result = await response.json();
        const settingsDoc = result?.data || null;
        if (!isMounted) return;
        setDefaultRegenerationProvider(normalizeProviderValue(settingsDoc?.REGEN_DEFAULT_DYNAMIC_PROVIDER || 'vertex') || 'vertex');
        setDefaultRegenerationModel(String(settingsDoc?.REGEN_DEFAULT_MODEL || '').trim());
        setRegenerationModelOptions(extractEnabledModelOptions(settingsDoc || {}));
      } catch (_) {
        if (!isMounted) return;
        setDefaultRegenerationProvider('vertex');
        setDefaultRegenerationModel('');
        setRegenerationModelOptions([]);
      }
    };

    loadRegenerationModelSettings();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !selectedGeneration?.generation_id) {
      setGenerationDetails(null);
      return;
    }

    let isMounted = true;
    generationsService.getGenerationByGenerationId(selectedGeneration.generation_id)
      .then((generation) => {
        if (isMounted) setGenerationDetails(generation);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, selectedGeneration?.generation_id]);

  // Sync gift box image URL from generation
  useEffect(() => {
    if (selectedGeneration) {
      setGiftBoxImageUrl(selectedGeneration.gift_box_image_url || null);
    }
  }, [selectedGeneration]);

  const handleClose = () => {
    onClose();
    setCurrentGenerationImageIndex(0); // Reset index when closing
    setCustomPrompts({}); // Reset custom prompts
    setSelectedModelsBySceneKey({}); // Reset custom model selections
    setRegenerationMetaBySceneKey({}); // Reset regeneration metadata badges
    setGenerationCostOverride(null); // Reset live cost override
    setSceneVersionsBySceneKey({}); // Reset cached version strips
    setExpandedVersionsBySceneKey({}); // Collapse version strips
    setActivatingVersionIdBySceneKey({}); // Reset activation loading states
    setPreviewVersionIdBySceneKey({}); // Reset version previews
    setExpandedCustomPrompt({}); // Reset expanded states
    setGenerationDetails(null);
  };

  const handleDownloadReadablePdf = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsDownloadingReadablePdf(true);
      try {
        // Call the PDF download endpoint
        const response = await fetch(`${SERVER_URL}/generations/download/readablepdf/${selectedGeneration.generation_id}`, {
          headers: authService.getAuthHeaders(),
        });

        if (!response.ok) {
          // Try to parse the error response body
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }

          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
          return;
        }

        const result = await response.json();

        if (result.success && result.data && result.data.pdf_readable_url) {
          // Open the PDF in a new window/tab
          const pdfUrl = result.data.pdf_readable_url;

          // Create a temporary link element to open in new window
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none'; // Hide the link

          // Append to body, click, and remove
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Show success message
          setToastMessage('PDF opened in new window');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);
        } else {
          // Handle error response from API
          const errorMessage = result.error || result.message || 'Failed to get PDF download link';
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to download PDF file:', error);
        const errorMessage = error.message || 'Failed to download PDF file';
        setToastMessage(`Download Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsDownloadingReadablePdf(false);
      }
    }
  };

  const handleDownloadReadablePdf38x19 = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsDownloadingReadablePdf38x19(true);
      try {
        const response = await fetch(`${SERVER_URL}/generations/download/readablepdf38x19/${selectedGeneration.generation_id}`, {
          headers: authService.getAuthHeaders(),
        });

        if (!response.ok) {
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }
          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
          return;
        }

        const result = await response.json();

        if (result.success && result.data && result.data.pdf_readable_url) {
          const pdfUrl = result.data.pdf_readable_url;
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setToastMessage('PDF (38×19) opened in new window');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);
        } else {
          const errorMessage = result.error || result.message || 'Failed to get PDF download link';
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to download PDF (38x19) file:', error);
        const errorMessage = error.message || 'Failed to download PDF file';
        setToastMessage(`Download Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsDownloadingReadablePdf38x19(false);
      }
    }
  };

  const openGenerationPdf = async ({
    endpoint,
    urlField,
    setLoading,
    successMessage,
    missingUrlError,
    requestError,
  }) => {
    if (!selectedGeneration?.generation_id) return;

    setLoading(true);
    try {
      const response = await fetch(`${SERVER_URL}/generations/download/${endpoint}/${selectedGeneration.generation_id}`, {
        headers: authService.getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.error || result.message || `HTTP error! status: ${response.status}`);
      }

      const pdfUrl = result.data?.[urlField];
      if (!result.success || !pdfUrl) {
        throw new Error(result.error || result.message || missingUrlError);
      }

      const link = document.createElement('a');
      link.href = pdfUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setToastMessage(successMessage);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
    } catch (error) {
      console.error(requestError, error);
      setToastMessage(`Download Error: ${error.message || requestError}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPrintedPdf = () => openGenerationPdf({
    endpoint: 'montagedpdf',
    urlField: 'pdf_montaged_url',
    setLoading: setIsDownloadingPrintedPdf,
    successMessage: 'PDF opened in new window',
    missingUrlError: 'Failed to get PDF download link',
    requestError: 'Failed to download PDF file',
  });

  const handleGenerateMontage = async () => {
    if (!selectedGeneration?.generation_id) return;
    setIsSubmittingMontage(true);
    try {
      const submission = await montageJobsService.create(
        selectedGeneration.generation_id,
        montageType
      );
      setMontageJob(submission);
      setToastMessage('Montage job started');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
    } catch (error) {
      setToastMessage(`Montage Error: ${error.message}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setIsSubmittingMontage(false);
    }
  };

  const saveMontageArtifact = async (type, artifact) => {
    const { blob, filename } = await montageJobsService.downloadArtifact(
      selectedGeneration.generation_id,
      type,
      artifact
    );
    saveBlobDownload(blob, filename);
  };

  const downloadMontageArtifact = async (artifact) => {
    setDownloadingMontageArtifact(artifact);
    try {
      await saveMontageArtifact(montageType, artifact);
      setToastMessage('Montage PDF downloaded');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
    } catch (error) {
      setToastMessage(`Download Error: ${error.message}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setDownloadingMontageArtifact(null);
    }
  };

  const handleDownloadMontagedBookletPdf = async () => {
    if (!selectedGeneration?.generation_id) return;

    setIsDownloadingMontagedBookletPdf(true);
    try {
      const latestBooklet = await montageJobsService.get(
        selectedGeneration.generation_id,
        'booklet'
      );
      const latestUrl = latestBooklet?.artifacts?.inner?.url;
      if (latestBooklet?.status === 'completed' && latestUrl) {
        await saveMontageArtifact('booklet', 'inner');
        setToastMessage('Latest booklet montage PDF downloaded');
        setToastActive(true);
        setTimeout(() => setToastActive(false), 3000);
        return;
      }
    } catch (error) {
      console.warn('Latest booklet montage is unavailable, using legacy download', error);
    } finally {
      setIsDownloadingMontagedBookletPdf(false);
    }

    return openGenerationPdf({
      endpoint: 'montagedpdf-booklet',
      urlField: 'pdf_montaged_booklet_url',
      setLoading: setIsDownloadingMontagedBookletPdf,
      successMessage: 'Booklet montage PDF opened in new window',
      missingUrlError: 'Failed to get booklet montage PDF download link',
      requestError: 'Failed to download booklet montage PDF',
    });
  };

  const handleDownloadMontagedPdfNoCover = () => openGenerationPdf({
    endpoint: 'montagedpdf-nocover',
    urlField: 'pdf_montaged_nocover_url',
    setLoading: setIsDownloadingMontagedPdfNoCover,
    successMessage: 'Montaged PDF (No Cover) opened in new window',
    missingUrlError: 'Failed to get montaged PDF (no cover) download link',
    requestError: 'Failed to download montaged PDF (no cover)',
  });

  const handleDownloadPrintedPdfNoMontage = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsDownloadingPrintedPdfNoMontage(true);
      try {
        // Call the PDF download endpoint (non-montaged version)
        const response = await fetch(`${SERVER_URL}/generations/download/printedpdf/${selectedGeneration.generation_id}`, {
          headers: authService.getAuthHeaders(),
        });

        if (!response.ok) {
          // Try to parse the error response body
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }

          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
          return;
        }

        const result = await response.json();

        if (result.success && result.data && result.data.pdf_printed_url) {
          // Open the PDF in a new window/tab
          const pdfUrl = result.data.pdf_printed_url;

          // Create a temporary link element to open in new window
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none'; // Hide the link

          // Append to body, click, and remove
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Show success message
          setToastMessage('PDF opened in new window');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);
        } else {
          // Handle error response from API
          const errorMessage = result.error || result.message || 'Failed to get PDF download link';
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to download PDF file:', error);
        const errorMessage = error.message || 'Failed to download PDF file';
        setToastMessage(`Download Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsDownloadingPrintedPdfNoMontage(false);
      }
    }
  };

  const handleApprove = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsApproving(true);
      try {
        // Call the approve endpoint
        const response = await fetch(`${SERVER_URL}/generations/${selectedGeneration.generation_id}/approve`, {
          method: 'PATCH',
          headers: authService.getAuthHeaders(),
        });

        if (!response.ok) {
          // Try to parse the error response body
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }

          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
          setToastMessage(`Approve Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
          return;
        }

        const result = await response.json();

        if (result.success) {
          // Show success message
          setToastMessage('Generation approved successfully');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);

          // Update the selectedGeneration status locally
          if (selectedGeneration) {
            selectedGeneration.status = 'approved';
          }
        } else {
          // Handle error response from API
          const errorMessage = result.error || result.message || 'Failed to approve generation';
          setToastMessage(`Approve Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to approve generation:', error);
        const errorMessage = error.message || 'Failed to approve generation';
        setToastMessage(`Approve Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsApproving(false);
      }
    }
  };

  // Face approval handler
  const handleApproveFace = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsApprovingFace(true);
      try {
        const result = await generationsService.approveFace(selectedGeneration.generation_id);

        if (result.success) {
          setToastMessage('Face approved! Scene generation started.');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);

          // Update the selectedGeneration status locally
          if (selectedGeneration) {
            selectedGeneration.status = 'pending';
          }
        } else {
          const errorMessage = result.error || result.message || 'Failed to approve face';
          setToastMessage(`Approve Face Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to approve face:', error);
        const errorMessage = error.message || 'Failed to approve face';
        setToastMessage(`Approve Face Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsApprovingFace(false);
      }
    }
  };

  // Face regeneration handler
  const handleRegenerateFace = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsRegeneratingFace(true);
      try {
        const result = await generationsService.regenerateFace(selectedGeneration.generation_id);

        if (result.success) {
          setToastMessage('Face regeneration started.');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);

          // Update the selectedGeneration status locally
          if (selectedGeneration) {
            selectedGeneration.status = 'face_generating';
          }
        } else {
          const errorMessage = result.error || result.message || 'Failed to regenerate face';
          setToastMessage(`Regenerate Face Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to regenerate face:', error);
        const errorMessage = error.message || 'Failed to regenerate face';
        setToastMessage(`Regenerate Face Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsRegeneratingFace(false);
      }
    }
  };

  // Cover trial generation handler
  const handleGenerateTrial = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsGeneratingTrial(true);
      try {
        const coverSceneKey = `${selectedGeneration.generation_id}::0`;
        const customModel = selectedModelsBySceneKey[coverSceneKey] || '';
        const result = await generationsService.generateTrial(selectedGeneration.generation_id, customModel);

        if (result.success) {
          setToastMessage('Cover trial generation started.');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);

          // Update the selectedGeneration status locally
          if (selectedGeneration) {
            selectedGeneration.status = 'trial';
          }
        } else {
          const errorMessage = result.error || result.message || 'Failed to generate cover trial';
          setToastMessage(`Generate Trial Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to generate cover trial:', error);
        const errorMessage = error.message || 'Failed to generate cover trial';
        setToastMessage(`Generate Trial Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsGeneratingTrial(false);
      }
    }
  };

  // Continue generation for the remaining scenes (keeps the generated trial cover)
  const handleContinueGeneration = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsContinuingGeneration(true);
      try {
        const result = await generationsService.regenerateMissingScenes(selectedGeneration.generation_id);

        if (result.success) {
          const missing = Array.isArray(result.missingOrders) && result.missingOrders.length > 0
            ? result.missingOrders.join(', ')
            : '';
          setToastMessage(missing ? `Continuing generation for scenes: ${missing}` : 'Continuing generation.');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);

          // Update the selectedGeneration status locally
          if (selectedGeneration) {
            selectedGeneration.status = 'pending';
          }
        } else {
          const errorMessage = result.error || result.message || 'Failed to continue generation';
          setToastMessage(`Continue Generation Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to continue generation:', error);
        const errorMessage = error.message || 'Failed to continue generation';
        setToastMessage(`Continue Generation Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsContinuingGeneration(false);
      }
    }
  };

  const handleRegenerateScene = async (targetScene) => {
    const currentScene = targetScene;
    if (!selectedGeneration || !currentScene) return;
    // if (currentScene?.type !== 'Scene' && currentScene?.type !== 'cover') return;

    const generationId = selectedGeneration.generation_id || '';
    const sceneOrder = String(currentScene.sceneOrder ?? '');
    const kid_image = selectedGeneration.kid_image || '';
    const story_slug = selectedGeneration.story_slug || (selectedGeneration.story && selectedGeneration.story.slug) || '';

    if (!generationId || !sceneOrder || !kid_image || !story_slug) {
      setToastMessage('Missing data to regenerate scene');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    const sceneKey = `${generationId}::${sceneOrder}`;
    if (activeSceneRegenerationsRef.current.has(sceneKey)) return;
    activeSceneRegenerationsRef.current.add(sceneKey);
    setRegeneratingBySceneOrder(prev => ({ ...prev, [sceneKey]: true }));
    try {
      const customPrompt = customPrompts[sceneKey] || '';
      const customModel = selectedModelsBySceneKey[sceneKey] || '';
      const resp = await fetch(`${SERVER_URL}/regenerate-scene`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ generationId, sceneOrder, kid_image, story_slug, customPrompt, customModel }),
      });
      let data = {};
      try { data = await resp.clone().json(); } catch { }
      if (!resp.ok) {
        const msg = data.error || data.message || `HTTP error! status: ${resp.status}`;
        throw new Error(msg);
      }
      const newUrl = data?.imageUrl || '';
      if (newUrl && sceneOrder) {
        setRegeneratedImageUrlsBySceneOrder((prev) => ({ ...prev, [sceneKey]: newUrl }));
      }
      if (data?.sceneVersion?.generationCost) {
        setGenerationCostOverride(data.sceneVersion.generationCost);
      }
      const providerUsed = String(data?.providerUsed || '').trim();
      const modelSource = String(data?.modelSource || '').trim();
      const resolvedModel = getResolvedModelLabel(data);
      if (providerUsed || modelSource || resolvedModel) {
        setRegenerationMetaBySceneKey((prev) => ({
          ...prev,
          [sceneKey]: {
            providerUsed,
            modelSource,
            resolvedModel,
          },
        }));
      }
      setToastMessage('Regeneration for scene ' + sceneOrder + ' Done');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 2000);
    } catch (err) {
      const msg = err?.message || 'Failed to regenerate scene';
      setToastMessage(`Error: ${msg}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
    } finally {
      activeSceneRegenerationsRef.current.delete(sceneKey);
      setRegeneratingBySceneOrder(prev => ({ ...prev, [sceneKey]: false }));
    }
  };

  const showToast = (message, duration = 3000) => {
    setToastMessage(message);
    setToastActive(true);
    setTimeout(() => setToastActive(false), duration);
  };

  const handleEditScene = async (targetScene) => {
    if (!selectedGeneration || !targetScene) return;
    const generationId = selectedGeneration.generation_id || '';
    const sceneOrder = String(targetScene.sceneOrder ?? '');
    const sceneKey = `${generationId}::${sceneOrder}`;
    const prompt = (editPromptsBySceneKey[sceneKey] || '').trim();
    if (!generationId || !sceneOrder) return;
    if (!prompt) {
      showToast('Enter an edit prompt first');
      return;
    }
    if (activeSceneEditsRef.current.has(sceneKey)) return;
    activeSceneEditsRef.current.add(sceneKey);
    setEditingBySceneKey(prev => ({ ...prev, [sceneKey]: true }));
    try {
      const resp = await fetch(`${SERVER_URL}/edit-scene`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          generationId,
          sceneOrder,
          prompt,
          customModel: editModelsBySceneKey[sceneKey] || '',
        }),
      });
      let data = {};
      try { data = await resp.clone().json(); } catch { }
      if (!resp.ok) {
        throw new Error(data.error || data.message || `HTTP error! status: ${resp.status}`);
      }
      if (data?.imageUrl) {
        setRegeneratedImageUrlsBySceneOrder(prev => ({ ...prev, [sceneKey]: data.imageUrl }));
      }
      if (data?.sceneVersion?.generationCost) {
        setGenerationCostOverride(data.sceneVersion.generationCost);
      }
      setSceneVersionsBySceneKey(prev => {
        const { [sceneKey]: _stale, ...rest } = prev;
        return rest;
      });
      setRegenerationMetaBySceneKey(prev => ({
        ...prev,
        [sceneKey]: {
          providerUsed: String(data?.providerUsed || '').trim(),
          modelSource: String(data?.modelSource || '').trim(),
          resolvedModel: getResolvedModelLabel(data),
        },
      }));
      setEditPromptsBySceneKey(prev => ({ ...prev, [sceneKey]: '' }));
      showToast(`Edit for scene ${sceneOrder} done`, 2000);
    } catch (err) {
      showToast(`Error: ${err?.message || 'Failed to edit scene'}`, 4000);
    } finally {
      activeSceneEditsRef.current.delete(sceneKey);
      setEditingBySceneKey(prev => ({ ...prev, [sceneKey]: false }));
    }
  };

  // Scenes activated from version history have sceneType 'Scene' and no `type`, so check both shapes
  const isImageScene = (scene) => {
    if (!scene) return false;
    if (scene.type === 'Scene') return true;
    const sceneType = String(scene.sceneType || '').toLowerCase();
    return sceneType === 'image' || sceneType === 'scene';
  };
  const isEditableScene = (scene) => isImageScene(scene);

  const renderEditToggleButton = (sceneKey, label = 'Edit') => (
    <Button
      onClick={() => setExpandedEditBySceneKey(prev => ({ ...prev, [sceneKey]: !prev[sceneKey] }))}
    >
      {label}
    </Button>
  );

  const renderEditPanel = (scene, sceneKey, width) => (
    <Collapsible open={!!expandedEditBySceneKey[sceneKey]} id={`edit-scene-${sceneKey}`}>
      <div style={{ marginTop: '8px', width, textAlign: 'left' }}>
        <BlockStack gap="200">
          <Select
            label="Edit Model"
            options={regenerationModelSelectOptions}
            value={editModelsBySceneKey[sceneKey] || ''}
            onChange={(value) => setEditModelsBySceneKey(prev => ({ ...prev, [sceneKey]: value }))}
          />
          <TextField
            label="Edit Prompt"
            value={editPromptsBySceneKey[sceneKey] || ''}
            onChange={(val) => setEditPromptsBySceneKey(prev => ({ ...prev, [sceneKey]: val }))}
            multiline={3}
            autoComplete="off"
            helpText="Describe the small change to make. The current generated scene is used as the starting image."
          />
          <Button
            variant="primary"
            onClick={() => handleEditScene(scene)}
            loading={!!editingBySceneKey[sceneKey]}
            disabled={!(editPromptsBySceneKey[sceneKey] || '').trim()}
          >
            Apply Edit
          </Button>
        </BlockStack>
      </div>
    </Collapsible>
  );

  const renderRegenerationMetaBadges = (sceneKey) => {
    const meta = regenerationMetaBySceneKey[sceneKey];
    if (!meta) return null;
    const providerText = providerLabel(meta.providerUsed);
    const modelLabel = meta.resolvedModel || 'provider default';
    const sourceLabel = meta.modelSource ? `Source: ${meta.modelSource}` : '';

    return (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
        <span style={badgeStyle}>{`Provider: ${providerText}`}</span>
        <span style={badgeStyle}>{`Model: ${modelLabel}`}</span>
        {sourceLabel ? <span style={badgeStyle}>{sourceLabel}</span> : null}
      </div>
    );
  };

  const getVersionsSceneKey = (generationId, sceneOrder) => `${generationId || ''}::${sceneOrder || ''}`;

  const handleToggleVersions = async (scene) => {
    const generationId = selectedGeneration?.generation_id || '';
    const sceneOrder = String(scene?.sceneOrder ?? '');
    if (!generationId || !sceneOrder) return;
    const sceneKey = getVersionsSceneKey(generationId, sceneOrder);
    const isOpen = !!expandedVersionsBySceneKey[sceneKey];
    setExpandedVersionsBySceneKey((prev) => ({ ...prev, [sceneKey]: !isOpen }));
    if (isOpen || sceneVersionsBySceneKey[sceneKey]?.versions?.length) return;
    setSceneVersionsBySceneKey((prev) => ({ ...prev, [sceneKey]: { versions: [], loading: true } }));
    try {
      const versions = await getSceneVersions(generationId, sceneOrder);
      setSceneVersionsBySceneKey((prev) => ({ ...prev, [sceneKey]: { versions, loading: false } }));
    } catch (err) {
      setSceneVersionsBySceneKey((prev) => ({ ...prev, [sceneKey]: { versions: [], loading: false } }));
      setToastMessage(`Error: ${err?.message || 'Failed to load scene versions'}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
    }
  };

  const getVersionCountLabel = (scene) => {
    const generationId = selectedGeneration?.generation_id || '';
    const sceneOrder = String(scene?.sceneOrder ?? '');
    const count = sceneVersionsBySceneKey[getVersionsSceneKey(generationId, sceneOrder)]?.versions?.length || 0;
    return count ? ` (${count})` : '';
  };

  const getPreviewingVersion = (sceneKey) => {
    const previewId = previewVersionIdBySceneKey[sceneKey];
    if (!previewId) return null;
    return (sceneVersionsBySceneKey[sceneKey]?.versions || []).find((item) => item.version_id === previewId) || null;
  };

  const handleToggleVersionPreview = (scene, version) => {
    if (version?.status !== 'completed' || !version?.image_url) return;
    const generationId = selectedGeneration?.generation_id || '';
    const sceneOrder = String(scene?.sceneOrder ?? '');
    const sceneKey = getVersionsSceneKey(generationId, sceneOrder);
    setPreviewVersionIdBySceneKey((prev) => ({
      ...prev,
      [sceneKey]: prev[sceneKey] === version.version_id ? '' : version.version_id,
    }));
  };

  const handleActivateVersion = async (scene, version) => {
    const generationId = selectedGeneration?.generation_id || '';
    const sceneOrder = String(scene?.sceneOrder ?? '');
    if (!generationId || !sceneOrder || !version?.version_id) return;
    const sceneKey = getVersionsSceneKey(generationId, sceneOrder);
    setActivatingVersionIdBySceneKey((prev) => ({ ...prev, [sceneKey]: version.version_id }));
    try {
      const data = await activateSceneVersion(generationId, sceneOrder, version.version_id);
      if (data?.generationCost) {
        setGenerationCostOverride(data.generationCost);
      }
      if (data?.imageUrl) {
        setRegeneratedImageUrlsBySceneOrder((prev) => ({ ...prev, [sceneKey]: data.imageUrl }));
      }
      setSceneVersionsBySceneKey((prev) => ({
        ...prev,
        [sceneKey]: {
          ...prev[sceneKey],
          versions: (prev[sceneKey]?.versions || []).map((item) => ({
            ...item,
            is_active: item.version_id === version.version_id,
          })),
        },
      }));
      setToastMessage(`Scene ${sceneOrder} version activated`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 2000);
    } catch (err) {
      setToastMessage(`Error: ${err?.message || 'Failed to activate scene version'}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 4000);
    } finally {
      setActivatingVersionIdBySceneKey((prev) => ({ ...prev, [sceneKey]: '' }));
      setPreviewVersionIdBySceneKey((prev) => ({ ...prev, [sceneKey]: '' }));
    }
  };

  const renderVersionsStrip = (scene) => {
    const generationId = selectedGeneration?.generation_id || '';
    const sceneOrder = String(scene?.sceneOrder ?? '');
    const sceneKey = getVersionsSceneKey(generationId, sceneOrder);
    const entry = sceneVersionsBySceneKey[sceneKey];
    const isOpen = !!expandedVersionsBySceneKey[sceneKey];
    const activatingVersionId = activatingVersionIdBySceneKey[sceneKey] || '';
    const previewingVersionId = previewVersionIdBySceneKey[sceneKey] || '';
    return (
      <Collapsible open={isOpen} id={`scene-versions-${sceneKey}`}>
        <div style={{ width: '100%', textAlign: 'left', marginTop: '8px' }}>
          {entry?.loading ? (
            <InlineStack align="center" blockAlign="center" gap="200">
              <Spinner size="small" />
              <Text variant="bodySm" tone="subdued">Loading versions...</Text>
            </InlineStack>
          ) : !entry?.versions?.length ? (
            <Text variant="bodySm" tone="subdued">No recorded versions for this scene yet.</Text>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '4px' }}>
              {entry.versions.map((version) => (
                <div
                  key={version.version_id}
                  style={{
                    width: 132,
                    padding: '6px',
                    borderRadius: '8px',
                    border: version.is_active ? '2px solid #00a97f' : (previewingVersionId === version.version_id ? '2px solid #2c6ecb' : '1px solid #dcdfe4'),
                    backgroundColor: version.is_active ? '#f1fdf9' : (previewingVersionId === version.version_id ? '#f4f6fa' : '#ffffff'),
                    cursor: version.status === 'completed' && version.image_url ? 'pointer' : 'default',
                  }}
                  onClick={() => handleToggleVersionPreview(scene, version)}
                  title={version.status === 'completed' && version.image_url ? 'Click to preview this version' : undefined}
                >
                  <div style={{ width: '100%', height: 96, borderRadius: 6, overflow: 'hidden', marginBottom: 4, pointerEvents: 'none' }}>
                    <AuthenticatedAssetImage
                      src={getPreviewUrl(version.image_url)}
                      fallbackSrc={version.image_url}
                      assetSize="thumbnail"
                      alt={`Version ${version.version_id}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                  <InlineStack gap="100" blockAlign="center">
                    <Text variant="bodySm" fontWeight="semibold">
                      {new Date(version.created_at).toLocaleDateString()}
                    </Text>
                    {version.is_active ? <span style={{ ...badgeStyle, color: '#00a97f' }}>Active</span> : null}
                    {previewingVersionId === version.version_id ? <span style={{ ...badgeStyle, color: '#2c6ecb' }}>Previewing</span> : null}
                    {version.status === 'failed' ? <span style={{ ...badgeStyle, color: '#d72c0d' }}>Failed</span> : null}
                  </InlineStack>
                  <Text variant="bodySm" tone="subdued" breakWord>
                    {`${providerLabel(version.provider)} · ${version.model_label || version.model_value || 'provider default'}`}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {`${version.cost?.currency || 'USD'} ${Number(version.cost?.estimated_amount || 0).toFixed(4)}`}
                  </Text>
                  {version.status === 'completed' && !version.is_active ? (
                    <Button
                      size="slim"
                      fullWidth
                      loading={activatingVersionId === version.version_id}
                      onClick={(e) => { e?.stopPropagation?.(); handleActivateVersion(scene, version); }}
                    >
                      Set active
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </Collapsible>
    );
  };

  const handleDownloadCoverPdf = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsDownloadingCoverPdf(true);
      try {
        const response = await fetch(`${SERVER_URL}/generations/download/coverpdf/${selectedGeneration.generation_id}`, {
          headers: authService.getAuthHeaders(),
        });

        if (!response.ok) {
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }
          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
          return;
        }

        const result = await response.json();

        if (result.success && result.data && result.data.pdf_cover_url) {
          const pdfUrl = result.data.pdf_cover_url;
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setToastMessage('Cover PDF (A3) opened in new window');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);
        } else {
          const errorMessage = result.error || result.message || 'Failed to get cover PDF download link';
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to download cover PDF:', error);
        const errorMessage = error.message || 'Failed to download cover PDF';
        setToastMessage(`Download Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsDownloadingCoverPdf(false);
      }
    }
  };

  const handleDownloadReadablePdfNocover = async () => {
    if (selectedGeneration && selectedGeneration.generation_id) {
      setIsDownloadingReadablePdfNocover(true);
      try {
        const response = await fetch(`${SERVER_URL}/generations/download/readablepdf-nocover/${selectedGeneration.generation_id}`, {
          headers: authService.getAuthHeaders(),
        });

        if (!response.ok) {
          let errorData = {};
          try {
            errorData = await response.json();
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
          }
          const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
          return;
        }

        const result = await response.json();

        if (result.success && result.data && result.data.pdf_readable_nocover_url) {
          const pdfUrl = result.data.pdf_readable_nocover_url;
          const link = document.createElement('a');
          link.href = pdfUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setToastMessage('Readable PDF (No Cover) opened in new window');
          setToastActive(true);
          setTimeout(() => setToastActive(false), 3000);
        } else {
          const errorMessage = result.error || result.message || 'Failed to get PDF download link';
          setToastMessage(`Download Error: ${errorMessage}`);
          setToastActive(true);
          setTimeout(() => setToastActive(false), 5000);
        }
      } catch (error) {
        console.error('Failed to download readable PDF (no cover):', error);
        const errorMessage = error.message || 'Failed to download PDF file';
        setToastMessage(`Download Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => setToastActive(false), 5000);
      } finally {
        setIsDownloadingReadablePdfNocover(false);
      }
    }
  };

  const previewGeneration = useMemo(() => ({
    ...(selectedGeneration || {}),
    ...(generationDetails || {}),
    story: selectedGeneration?.story || generationDetails?.story,
  }), [selectedGeneration, generationDetails]);

  const groupedScenes = useMemo(() => {
    const scenes = buildGenerationScenePreviewItems(previewGeneration);
    // In trial status only the cover scene exists so far — hide the rest until Continue Generation
    const isTrialStatus = String(previewGeneration?.status || '').toLowerCase() === 'trial';
    const visibleScenes = isTrialStatus
      ? scenes.filter(scene => String(scene?.sceneOrder) === '0' || scene?.sceneOrder === 0)
      : scenes;
    return groupGenerationPreviewScenes(visibleScenes, previewGeneration);
  }, [previewGeneration]);

  if (!selectedGeneration) return null;

  const storyTitle = String(
    selectedGeneration?.story?.title ||
    selectedGeneration?.story_title ||
    selectedGeneration?.story?.slug ||
    selectedGeneration?.story_slug ||
    ''
  ).trim() || 'Unknown story';
  const storyProviderOverride = String(
    selectedGeneration?.story?.regeneration_provider_override ||
    selectedGeneration?.regeneration_provider_override ||
    ''
  ).trim();
  const storyModelOverride = String(
    selectedGeneration?.story?.regeneration_model_override ||
    selectedGeneration?.regeneration_model_override ||
    ''
  ).trim();
  const effectiveStoryProvider = normalizeProviderValue(storyProviderOverride) || defaultRegenerationProvider || 'vertex';
  const effectiveStoryModel = storyModelOverride || defaultRegenerationModel || 'provider default';
  const generationCost = generationCostOverride
    || generationDetails?.generation_cost
    || selectedGeneration?.generation_cost
    || null;
  const generationCostLabel = generationCost
    ? `Est. Cost: ${generationCost.currency || 'USD'} ${Number(generationCost.estimated_total || 0).toFixed(4)} · ${generationCost.version_count ?? 0} versions`
    : '';

  return (
    <>
      <style>{`
      .view-generation-modal-open .Polaris-Modal-Dialog__Modal { max-width: 80% !important; }
    `}</style>
      <Modal
        open={isOpen}
        onClose={handleClose}
        title={
          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <span>Generated Scenes Preview</span>
              <span style={badgeStyle}>{`Story: ${storyTitle}`}</span>
              <span style={badgeStyle}>{`Provider: ${providerLabel(effectiveStoryProvider)}`}</span>
              <span style={badgeStyle}>{`Model: ${effectiveStoryModel}`}</span>
              {generationCostLabel ? <span style={badgeStyle}>{generationCostLabel}</span> : null}
            </div>
          </div>
        }
        primaryAction={{
          content: 'Close',
          onAction: handleClose,
        }}
        secondaryActions={(() => {
          const status = selectedGeneration?.status;
          const actions = [];

          // Show Approve button when status is "completed"
          if (status === 'completed') {
            actions.push({
              content: 'Approve',
              icon: <Icon source={CircleTickMajor} />,
              loading: isApproving,
              onAction: handleApprove,
            });
          }

          // Show Face Approve/Regenerate buttons when status is "face_pending"
          if (status === 'face_pending') {
            actions.push(
              {
                content: 'Approve Face & Continue',
                icon: <Icon source={CircleTickMajor} />,
                loading: isApprovingFace,
                onAction: handleApproveFace,
              },
              {
                content: 'Generate Trial',
                icon: <Icon source={MagicMajor} />,
                loading: isGeneratingTrial,
                onAction: handleGenerateTrial,
              },
              {
                content: 'Regenerate Face',
                icon: <Icon source={RefreshMajor} />,
                loading: isRegeneratingFace,
                onAction: handleRegenerateFace,
              }
            );
          }

          // Show trial actions when status is "trial"
          if (status === 'trial') {
            actions.push(
              {
                content: 'Continue Generation',
                icon: <Icon source={CircleTickMajor} />,
                loading: isContinuingGeneration,
                onAction: handleContinueGeneration,
              },
              {
                content: 'Regenerate Trial Cover',
                icon: <Icon source={MagicMajor} />,
                loading: isGeneratingTrial,
                onAction: handleGenerateTrial,
              },
              {
                content: 'Regenerate Face',
                icon: <Icon source={RefreshMajor} />,
                loading: isRegeneratingFace,
                onAction: handleRegenerateFace,
              }
            );
          }

          // Show download buttons only when status is "approved"
          if (status === 'approved' || status === 'out_for_printing' || status === 'printed') {
            actions.push(
              {
                content: 'Story Cover',
                icon: <Icon source={FileMinor} />,
                loading: isDownloadingCoverPdf,
                onAction: handleDownloadCoverPdf,
              }
            );
            if (isDynamicStory) {
              const defaultProfileKey = selectedGeneration?.story?.default_montage_profile
                || generationDetails?.story?.default_montage_profile;
              const isColoringBookA4 = defaultProfileKey === 'coloring-book-a4';
              const noCoverLabel = isColoringBookA4
                ? 'Single A4 Sheets'
                : 'Montaged PDF (No Cover)';
              actions.push(
                {
                  content: noCoverLabel,
                  icon: <Icon source={FileMinor} />,
                  loading: isDownloadingMontagedPdfNoCover,
                  onAction: handleDownloadMontagedPdfNoCover,
                }
              );
              if (!isColoringBookA4) {
                actions.push(
                  {
                    content: 'Booklet Montage PDF',
                    icon: <Icon source={FileMinor} />,
                    loading: isDownloadingMontagedBookletPdf,
                    onAction: handleDownloadMontagedBookletPdf,
                  }
                );
              }
            } else {
              actions.push(
                {
                  content: 'Instant Readable PDF',
                  icon: <Icon source={FileMinor} />,
                  loading: isDownloadingReadablePdf,
                  onAction: handleDownloadReadablePdf,
                },
                {
                  content: 'Readable PDF (No Cover)',
                  icon: <Icon source={FileMinor} />,
                  loading: isDownloadingReadablePdfNocover,
                  onAction: handleDownloadReadablePdfNocover,
                },
              {
                content: 'Montaged Story PDF',
                icon: <Icon source={FileMinor} />,
                loading: isDownloadingPrintedPdf,
                onAction: handleDownloadPrintedPdf,
                }
              );
            }
          }

          // Show Gift Box button for gift orders
          if (selectedGeneration?.is_gift) {
            const giftBoxIsProcessing = selectedGeneration?.gift_box_status === 'queued' || selectedGeneration?.gift_box_status === 'generating';
            actions.push({
              content: giftBoxImageUrl ? '🎁 View Gift Box' :
                giftBoxIsProcessing ? '⏳ Generating...' :
                  '🎁 Generate Gift Box',
              loading: isGeneratingGiftBox || giftBoxIsProcessing,
              onAction: async () => {
                if (giftBoxImageUrl) {
                  // Prefer PDF if available, otherwise open image
                  const pdfUrl = selectedGeneration?.gift_box_pdf_url;
                  window.open(pdfUrl || giftBoxImageUrl, '_blank');
                  return;
                }
                if (giftBoxIsProcessing) return; // Already in queue
                const genId = selectedGeneration?.generation_id;
                if (!genId) return;
                setIsGeneratingGiftBox(true);
                try {
                  const result = await generationsService.generateGiftBox(genId);
                  if (result?.success) {
                    setToastMessage('Gift box generation queued!');
                    setToastActive(true);
                    setTimeout(() => setToastActive(false), 3000);
                  } else {
                    throw new Error(result?.error || 'Failed to queue');
                  }
                } catch (err) {
                  setToastMessage(`Gift Box Error: ${err?.message || 'Failed to queue'}`);
                  setToastActive(true);
                  setTimeout(() => setToastActive(false), 5000);
                } finally {
                  setIsGeneratingGiftBox(false);
                }
              },
            });
          }

          // Show Gift Note button for gift note orders
          if (selectedGeneration?.has_gift_note) {
            const giftNoteIsProcessing = selectedGeneration?.gift_note_status === 'processing';
            const giftNotePdfUrl = selectedGeneration?.gift_note_pdf_url;
            actions.push({
              content: giftNotePdfUrl ? '📝 View Gift Note' :
                giftNoteIsProcessing ? '⏳ Note Generating...' :
                  '📝 Generate Gift Note',
              loading: giftNoteIsProcessing,
              onAction: async () => {
                if (giftNotePdfUrl) {
                  window.open(giftNotePdfUrl, '_blank');
                  return;
                }
                if (giftNoteIsProcessing) return;
                const genId = selectedGeneration?.generation_id;
                if (!genId) return;
                try {
                  const result = await generationsService.generateGiftNote(genId);
                  if (result?.success) {
                    setToastMessage('Gift note generation started!');
                    setToastActive(true);
                    setTimeout(() => setToastActive(false), 3000);
                  } else {
                    throw new Error(result?.error || 'Failed to start');
                  }
                } catch (err) {
                  setToastMessage(`Gift Note Error: ${err?.message || 'Failed to start'}`);
                  setToastActive(true);
                  setTimeout(() => setToastActive(false), 5000);
                }
              },
            });
          }

          return actions;
        })()}
        footer={
          groupedScenes && groupedScenes.length > 1 ? (
            <div style={{ padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
              <Button
                icon={ChevronLeftMinor}
                size="slim"
                onClick={() => setCurrentGenerationImageIndex(prev =>
                  prev === 0 ? groupedScenes.length - 1 : prev - 1
                )}
              />
              <Text variant="bodyMd" tone="subdued">
                {currentGenerationImageIndex + 1} of {groupedScenes.length}
              </Text>
              <Button
                icon={ChevronRightMinor}
                size="slim"
                onClick={() => setCurrentGenerationImageIndex(prev =>
                  prev === groupedScenes.length - 1 ? 0 : prev + 1
                )}
              />
            </div>
          ) : (
            <div style={{ padding: '16px' }}></div>
          )
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '20% 80%', gap: '16px', alignItems: 'center', alignContent: 'center', minHeight: '70vh' }}>
          {/* Left fixed kid image + face image */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
            {['approved', 'out_for_printing', 'printed'].includes(selectedGeneration?.status) && (
              <div style={{ width: '100%', padding: 12, border: '1px solid #dfe3e8', borderRadius: 8 }}>
                <BlockStack gap="200">
                  <Text variant="headingSm">Montage service</Text>
                  <Select
                    label="Montage type"
                    options={montageTypeOptions}
                    value={montageType}
                    onChange={(value) => {
                      setMontageType(value);
                      setMontageJob(null);
                    }}
                  />
                  <Button
                    onClick={handleGenerateMontage}
                    loading={isSubmittingMontage}
                  >
                    Generate montage
                  </Button>
                  {montageJob?.status && (
                    <Text variant="bodySm" tone="subdued">
                      Status: {montageJob.status}
                    </Text>
                  )}
                  {montageJob?.artifacts?.inner?.url && (
                    <Button
                      size="slim"
                      loading={downloadingMontageArtifact === 'inner'}
                      disabled={downloadingMontageArtifact !== null}
                      onClick={() => downloadMontageArtifact('inner')}
                    >
                      Download inner PDF
                    </Button>
                  )}
                  {montageJob?.artifacts?.cover?.url && (
                    <Button
                      size="slim"
                      loading={downloadingMontageArtifact === 'cover'}
                      disabled={downloadingMontageArtifact !== null}
                      onClick={() => downloadMontageArtifact('cover')}
                    >
                      Download cover PDF
                    </Button>
                  )}
                </BlockStack>
              </div>
            )}
            <div style={{ width: '100%' }}>
              <Text variant="bodySm" tone="subdued" alignment="center">Kid Image</Text>
              <div style={{
                position: 'relative',
                width: '100%',
                paddingBottom: '100%', /* square */
                backgroundColor: '#f6f6f7',
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid #ebeef0',
                marginTop: 4,
              }}>
                {selectedGeneration.kid_image ? (
                  <AuthenticatedAssetImage
                    src={selectedGeneration.kid_image}
                    assetSize="thumbnail"
                    alt={selectedGeneration.kid_name || 'Kid Image'}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BlockStack gap="100" align="center">
                      <Icon source={ImageMajor} color="subdued" />
                      <Text variant="bodySm" tone="subdued">No kid image</Text>
                    </BlockStack>
                  </div>
                )}
              </div>
            </div>
            {selectedGeneration.face_image_url && (
              <div style={{ width: '100%' }}>
                <Text variant="bodySm" tone="subdued" alignment="center">Generated Face</Text>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  paddingBottom: '100%',
                  backgroundColor: '#f6f6f7',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid #d4e5d9',
                  marginTop: 4,
                }}>
                  <AuthenticatedAssetImage
                    src={selectedGeneration.face_image_url}
                    assetSize="thumbnail"
                    alt="Generated Face"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>
            )}
            {/* Gift Box Thumbnail */}
            {(selectedGeneration.is_gift || selectedGeneration.has_gift_note) && giftBoxImageUrl && (
              <div style={{ width: '100%' }}>
                <Text variant="bodySm" tone="subdued" alignment="center">🎁 Gift Box</Text>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  paddingBottom: '100%',
                  backgroundColor: '#fff8f0',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '2px solid #f0c040',
                  marginTop: 4,
                  cursor: 'pointer',
                }} onClick={() => giftBoxImageUrl && window.open(giftBoxImageUrl, '_blank')}>
                  <AuthenticatedAssetImage
                    src={giftBoxImageUrl}
                    assetSize="thumbnail"
                    alt="Gift Box"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right: scenes carousel/content or face preview */}
          <div>
            {/* Face Pending State - Show generated face for approval */}
            {selectedGeneration?.status === 'face_pending' && selectedGeneration?.face_image_url ? (
              <BlockStack gap="400">
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '70vh',
                  minHeight: '520px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f6f6f7',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <AuthenticatedAssetImage
                    src={selectedGeneration.face_image_url}
                    assetSize="preview"
                    alt="Generated Face"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Text variant="headingMd">Generated Face Preview</Text>
                  <Text variant="bodyMd" tone="subdued">
                    Review the generated face. Click "Approve Face & Continue" to proceed with full scene generation, "Generate Trial" to generate the story cover only, or "Regenerate Face" to try again.
                  </Text>
                </div>
              </BlockStack>
            ) : selectedGeneration?.status === 'face_generating' ? (
              /* Face Generating State - Show loading */
              <BlockStack gap="400" align="center">
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '70vh',
                  minHeight: '520px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f6f6f7',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <BlockStack gap="400" align="center">
                    <Spinner size="large" />
                    <Text variant="headingMd">Generating Face...</Text>
                    <Text variant="bodyMd" tone="subdued">
                      Please wait while we generate the kid's face. This may take a few moments.
                    </Text>
                  </BlockStack>
                </div>
              </BlockStack>
            ) : groupedScenes && groupedScenes.length > 0 ? (
              <BlockStack gap="400">
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '70vh',
                  minHeight: '520px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f6f6f7',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  {(function () {
                    const group = groupedScenes[currentGenerationImageIndex] || [];
                    if (group.length === 1) {
                      const current = group[0];
                      const orderKey = String(current.sceneOrder ?? 0);
                      const genId = selectedGeneration.generation_id || '';
                      const sceneKey = `${genId}::${orderKey}`;
                      const scenePreviewVersion = getPreviewingVersion(sceneKey);
                      const fullUrl = scenePreviewVersion?.image_url || regeneratedImageUrlsBySceneOrder[sceneKey] || current.processedImageUrl;
                      const previewUrl = getPreviewUrl(fullUrl);
                      const isMissing = current.isMissing && !regeneratedImageUrlsBySceneOrder[sceneKey] && !scenePreviewVersion;
                      return (
                        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isMissing && <span style={missingBadgeStyle}>Missing</span>}
                          {scenePreviewVersion ? (
                            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                              <Button
                                size="slim"
                                onClick={() => setPreviewVersionIdBySceneKey((prev) => ({ ...prev, [sceneKey]: '' }))}
                              >
                                Exit preview
                              </Button>
                            </div>
                          ) : null}
                          {fullUrl ? (
                            <AuthenticatedAssetImage
                              src={previewUrl}
                              fallbackSrc={fullUrl}
                              assetSize="preview"
                              alt={isMissing ? `Missing scene ${current.sceneOrder ?? ''} mask` : `Scene ${current.sceneOrder ?? ''}`}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                opacity: isMissing ? 0.72 : 1
                              }}
                            />
                          ) : (
                            <Text variant="bodyMd" tone="subdued">Mask unavailable</Text>
                          )}
                        </div>
                      );
                    }
                    // Two scenes side by side
                    const left = group[0];
                    const right = group[1];
                    const genId = selectedGeneration.generation_id || '';
                    const leftKey = `${genId}::${String(left.sceneOrder ?? '')}`;
                    const rightKey = `${genId}::${String(right.sceneOrder ?? '')}`;
                    const leftPreviewVersion = getPreviewingVersion(leftKey);
                    const rightPreviewVersion = getPreviewingVersion(rightKey);
                    const leftFullUrl = leftPreviewVersion?.image_url || regeneratedImageUrlsBySceneOrder[leftKey] || left.processedImageUrl;
                    const rightFullUrl = rightPreviewVersion?.image_url || regeneratedImageUrlsBySceneOrder[rightKey] || right.processedImageUrl;
                    const leftPreviewUrl = getPreviewUrl(leftFullUrl);
                    const rightPreviewUrl = getPreviewUrl(rightFullUrl);
                    const isLeftMissing = left.isMissing && !regeneratedImageUrlsBySceneOrder[leftKey] && !leftPreviewVersion;
                    const isRightMissing = right.isMissing && !regeneratedImageUrlsBySceneOrder[rightKey] && !rightPreviewVersion;
                    return (
                      <div style={{ display: 'flex', width: '100%', height: '100%' }}>
                        <div style={{ position: 'relative', flex: 1, borderRight: '1px solid #ebeef0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isLeftMissing && <span style={missingBadgeStyle}>Missing</span>}
                          {leftPreviewVersion ? (
                            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                              <Button
                                size="slim"
                                onClick={() => setPreviewVersionIdBySceneKey((prev) => ({ ...prev, [leftKey]: '' }))}
                              >
                                Exit preview
                              </Button>
                            </div>
                          ) : null}
                          {leftFullUrl ? (
                            <AuthenticatedAssetImage
                              src={leftPreviewUrl}
                              fallbackSrc={leftFullUrl}
                              assetSize="preview"
                              alt={isLeftMissing ? `Missing scene ${left.sceneOrder ?? ''} mask` : `Scene ${left.sceneOrder ?? ''}`}
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', opacity: isLeftMissing ? 0.72 : 1 }}
                            />
                          ) : (
                            <Text variant="bodyMd" tone="subdued">Mask unavailable</Text>
                          )}
                        </div>
                        <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isRightMissing && <span style={missingBadgeStyle}>Missing</span>}
                          {rightPreviewVersion ? (
                            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                              <Button
                                size="slim"
                                onClick={() => setPreviewVersionIdBySceneKey((prev) => ({ ...prev, [rightKey]: '' }))}
                              >
                                Exit preview
                              </Button>
                            </div>
                          ) : null}
                          {rightFullUrl ? (
                            <AuthenticatedAssetImage
                              src={rightPreviewUrl}
                              fallbackSrc={rightFullUrl}
                              assetSize="preview"
                              alt={isRightMissing ? `Missing scene ${right.sceneOrder ?? ''} mask` : `Scene ${right.sceneOrder ?? ''}`}
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', opacity: isRightMissing ? 0.72 : 1 }}
                            />
                          ) : (
                            <Text variant="bodyMd" tone="subdued">Mask unavailable</Text>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div style={{ textAlign: 'center' }}>
                  {(function () {
                    const group = groupedScenes[currentGenerationImageIndex] || [];
                    if (group.length === 1) {
                      const current = group[0];
                      const currentSceneKey = `${selectedGeneration.generation_id || ''}::${String(current.sceneOrder ?? '')}`;
                      const isCurrentMissing = current.isMissing && !regeneratedImageUrlsBySceneOrder[currentSceneKey];
                      return (
                        <>
                          <Text variant="bodyMd" tone={isCurrentMissing ? 'critical' : undefined}>
                            {(() => {
                              const sOrder = current.sceneOrder;
                              if (isCurrentMissing) {
                                return `Missing Scene ${sOrder || ''}`;
                              }
                              if (sOrder === '0' || sOrder === 0) {
                                return 'Cover';
                              }
                              return `Scene ${sOrder || ''}`;
                            })()}
                          </Text>
                          <Text variant="bodyMd" tone="subdued">
                            {(() => {
                              const sceneType = current.sceneType;
                              const type = current.type;
                              if (current.sceneOrder === '0' || current.sceneOrder === 0) {
                                return 'Cover';
                              }
                              if (type === 'Scene') {
                                return 'Scene';
                              }
                              if (sceneType === 'text') {
                                return 'Text';
                              }
                              return sceneType || type || '';
                            })()}
                          </Text>
                          {(function () {
                            if (selectedGeneration.status !== 'completed' && !isCurrentMissing) return null;
                            const genId = selectedGeneration.generation_id || '';
                            const sceneKey = `${genId}::${String(current.sceneOrder ?? '')}`;
                            const isExpanded = isCurrentMissing || expandedCustomPrompt[sceneKey] || false;
                            return (
                              <BlockStack gap="200" align="center">
                                <div style={{ marginTop: 8 }}>
                                  <InlineStack gap="200" align="center">
                                    {!isCurrentMissing && (
                                      <Button
                                        onClick={() => {
                                          const willExpand = !isExpanded;
                                          setExpandedCustomPrompt(prev => ({ ...prev, [sceneKey]: willExpand }));
                                        }}
                                        plain
                                      >
                                        {isExpanded ? 'Hide Custom Face Prompt Option' : 'Edit Face Prompt (Optional)'}
                                      </Button>
                                    )}
                                    <Button
                                      icon={MagicMajor}
                                      onClick={() => handleRegenerateScene(current)}
                                      loading={!!regeneratingBySceneOrder[sceneKey]}
                                      disabled={isCurrentMissing && !current.canRegenerate}
                                    >
                                      {isCurrentMissing && !current.canRegenerate ? 'Mask Unavailable' : (isCurrentMissing ? 'Generate Missing Scene' : 'Regenerate')}
                                    </Button>
                                    <Button onClick={() => handleToggleVersions(current)}>{`Versions${getVersionCountLabel(current)}`}</Button>
                                    {!isCurrentMissing && isEditableScene(current) && renderEditToggleButton(sceneKey)}
                                  </InlineStack>
                                </div>
                                {renderRegenerationMetaBadges(sceneKey)}
                                {renderVersionsStrip(current)}
                                {!isCurrentMissing && isEditableScene(current) && renderEditPanel(current, sceneKey, '300px')}
                                <Collapsible open={isExpanded} id={`custom-prompt-${sceneKey}`}>
                                  <div style={{ marginTop: '8px', width: '300px', textAlign: 'left' }}>
                                    <Select
                                      label="Regeneration Model"
                                      options={regenerationModelSelectOptions}
                                      value={selectedModelsBySceneKey[sceneKey] || ''}
                                      onChange={(value) => setSelectedModelsBySceneKey(prev => ({ ...prev, [sceneKey]: value }))}
                                      helpText={defaultRegenerationModel ? `Default: ${defaultRegenerationModel}. Choose another model for this scene.` : 'Choose a model for this scene or keep the default.'}
                                    />
                                    <TextField
                                      label="Custom Face Extraction Prompt"
                                      value={customPrompts[sceneKey] || ''}
                                      onChange={(val) => setCustomPrompts(prev => ({ ...prev, [sceneKey]: val }))}
                                      multiline={3}
                                      helpText="Leave empty to use the default face prompt or cached face. Modifying this forces a new face generation."
                                    />
                                  </div>
                                </Collapsible>
                              </BlockStack>
                            );
                          })()}
                        </>
                      );
                    }
                    // Two scenes group labels and actions
                    const left = group[0];
                    const right = group[1];
                    const leftOrderKey = String(left.sceneOrder ?? '');
                    const rightOrderKey = String(right.sceneOrder ?? '');
                    const genIdStr = selectedGeneration.generation_id || '';
                    const leftSceneKey = `${genIdStr}::${leftOrderKey}`;
                    const rightSceneKey = `${genIdStr}::${rightOrderKey}`;
                    const isLeftMissing = left.isMissing && !regeneratedImageUrlsBySceneOrder[leftSceneKey];
                    const isRightMissing = right.isMissing && !regeneratedImageUrlsBySceneOrder[rightSceneKey];
                    const isLeftExpanded = isLeftMissing || expandedCustomPrompt[leftSceneKey] || false;
                    const isRightExpanded = isRightMissing || expandedCustomPrompt[rightSceneKey] || false;

                    return (
                      <div>
                        <Text variant="bodyMd">Scenes {left.sceneOrder ?? ''} and {right.sceneOrder ?? ''}</Text>
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
                          <InlineStack gap="400" align="center">
                            {((selectedGeneration.status === 'completed' && isImageScene(left)) || isLeftMissing) && (
                              <BlockStack gap="200" align="center">
                                <InlineStack gap="200">
                                  {!isLeftMissing && (
                                    <Button plain onClick={() => {
                                      const willExpand = !isLeftExpanded;
                                      setExpandedCustomPrompt(prev => ({ ...prev, [leftSceneKey]: willExpand }));
                                    }}>
                                      {isLeftExpanded ? 'Hide Custom Prompt' : 'Edit Face Prompt'}
                                    </Button>
                                  )}
                                  <Button
                                    icon={MagicMajor}
                                    onClick={() => handleRegenerateScene(left)}
                                    loading={!!regeneratingBySceneOrder[leftSceneKey]}
                                    disabled={isLeftMissing && !left.canRegenerate}
                                  >
                                    {isLeftMissing && !left.canRegenerate ? `Mask Unavailable ${left.sceneOrder}` : (isLeftMissing ? `Generate Missing ${left.sceneOrder}` : `Regen. ${left.sceneOrder}`)}
                                  </Button>
                                  <Button onClick={() => handleToggleVersions(left)}>{`Versions${getVersionCountLabel(left)}`}</Button>
                                  {!isLeftMissing && isEditableScene(left) && renderEditToggleButton(leftSceneKey, `Edit ${left.sceneOrder}`)}
                                </InlineStack>
                                {renderRegenerationMetaBadges(leftSceneKey)}
                                {renderVersionsStrip(left)}
                                {!isLeftMissing && isEditableScene(left) && renderEditPanel(left, leftSceneKey, '250px')}
                                <Collapsible open={isLeftExpanded} id={`custom-prompt-${leftSceneKey}`}>
                                  <div style={{ marginTop: '8px', width: '250px', textAlign: 'left' }}>
                                    <Select
                                      label={`Model (Scene ${left.sceneOrder})`}
                                      options={regenerationModelSelectOptions}
                                      value={selectedModelsBySceneKey[leftSceneKey] || ''}
                                      onChange={(value) => setSelectedModelsBySceneKey(prev => ({ ...prev, [leftSceneKey]: value }))}
                                    />
                                    <TextField
                                      label={`Face Prompt (Scene ${left.sceneOrder})`}
                                      value={customPrompts[leftSceneKey] || ''}
                                      onChange={(val) => setCustomPrompts(prev => ({ ...prev, [leftSceneKey]: val }))}
                                      multiline={3}
                                    />
                                  </div>
                                </Collapsible>
                              </BlockStack>
                            )}
                            {((selectedGeneration.status === 'completed' && isImageScene(right)) || isRightMissing) && (
                              <BlockStack gap="200" align="center">
                                <InlineStack gap="200">
                                  {!isRightMissing && (
                                    <Button plain onClick={() => {
                                      const willExpand = !isRightExpanded;
                                      setExpandedCustomPrompt(prev => ({ ...prev, [rightSceneKey]: willExpand }));
                                    }}>
                                      {isRightExpanded ? 'Hide Custom Prompt' : 'Edit Face Prompt'}
                                    </Button>
                                  )}
                                  <Button
                                    icon={MagicMajor}
                                    onClick={() => handleRegenerateScene(right)}
                                    loading={!!regeneratingBySceneOrder[rightSceneKey]}
                                    disabled={isRightMissing && !right.canRegenerate}
                                  >
                                    {isRightMissing && !right.canRegenerate ? `Mask Unavailable ${right.sceneOrder}` : (isRightMissing ? `Generate Missing ${right.sceneOrder}` : `Regen. ${right.sceneOrder}`)}
                                  </Button>
                                  <Button onClick={() => handleToggleVersions(right)}>{`Versions${getVersionCountLabel(right)}`}</Button>
                                  {!isRightMissing && isEditableScene(right) && renderEditToggleButton(rightSceneKey, `Edit ${right.sceneOrder}`)}
                                </InlineStack>
                                {renderRegenerationMetaBadges(rightSceneKey)}
                                {renderVersionsStrip(right)}
                                {!isRightMissing && isEditableScene(right) && renderEditPanel(right, rightSceneKey, '250px')}
                                <Collapsible open={isRightExpanded} id={`custom-prompt-${rightSceneKey}`}>
                                  <div style={{ marginTop: '8px', width: '250px', textAlign: 'left' }}>
                                    <Select
                                      label={`Model (Scene ${right.sceneOrder})`}
                                      options={regenerationModelSelectOptions}
                                      value={selectedModelsBySceneKey[rightSceneKey] || ''}
                                      onChange={(value) => setSelectedModelsBySceneKey(prev => ({ ...prev, [rightSceneKey]: value }))}
                                    />
                                    <TextField
                                      label={`Face Prompt (Scene ${right.sceneOrder})`}
                                      value={customPrompts[rightSceneKey] || ''}
                                      onChange={(val) => setCustomPrompts(prev => ({ ...prev, [rightSceneKey]: val }))}
                                      multiline={3}
                                    />
                                  </div>
                                </Collapsible>
                              </BlockStack>
                            )}
                          </InlineStack>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </BlockStack>
            ) : (
              <BlockStack gap="400" align="center">
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '70vh',
                  minHeight: '520px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f6f6f7',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <BlockStack gap="400" align="center">
                    <Icon source={ImageMajor} color="subdued" />
                    <Text variant="bodyMd" tone="subdued">No generated scenes available</Text>
                  </BlockStack>
                </div>
              </BlockStack>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ViewGenerationModal;
